import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import './SkillCard.css';

export interface SkillCardProps {
  title: string;
  description: string;
  /** Extra text the marketplace search matches, e.g. the skill's other-language description. */
  searchText?: string;
  tags: string[];
  skillName?: string;
  agentName?: string;
  enabled?: boolean;
  thumbnailUrl?: string;
  onToggle?: (skillName: string, agentName: string, equip: boolean) => Promise<void>;
  onShowDetails?: () => void;
}

export default function SkillCard({
  title,
  description,
  tags,
  skillName = '',
  agentName = '',
  enabled = false,
  onToggle,
  onShowDetails,
}: SkillCardProps) {
  const { t } = useTranslation('skills');
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    if (!onToggle || !skillName || toggling) return;
    setToggling(true);
    try {
      await onToggle(skillName, agentName, !enabled);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="skill-card">
      <div className="skill-card__header">
        <h3 className="skill-card__title">{title}</h3>
        <button
          className={`skill-card__toggle ${enabled ? 'skill-card__toggle--on' : ''} ${toggling ? 'skill-card__toggle--loading' : ''}`}
          onClick={handleToggle}
          disabled={toggling || !onToggle}
          aria-label={`${enabled ? 'Disable' : 'Enable'} ${title}`}
          role="switch"
          aria-checked={enabled}
        >
          <span className="skill-card__toggle-knob" />
        </button>
      </div>
      <p className="skill-card__desc">{description}</p>
      <div className="skill-card__tags">
        {tags.map((tag) => (
          <span key={tag} className="skill-card__badge">{tag}</span>
        ))}
      </div>
      <button className="skill-card__details" onClick={onShowDetails}>
        {t('card.showDetails')}
      </button>
    </div>
  );
}
